import { Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-gb';
import { useContext, useRef, useState } from 'react';
import '../../App.css';
import MTRLogoImage from '../../assets/images/mtr_logo.png';
import SnackbarContext from '../../components/SnackbarContext';
import { StyleConstants, Styles } from '../../Styles';
import { getDateTimeDisplay } from '../../utils/DateUtils';
import RestfulManager, { RES_STATUS_OK } from '../../manager/RestfulManager';
import HomePage from '../HomePage';
import Alert from '@mui/material/Alert';
import AppContext from '../../AppContext';
import useInterval from '../../hook/useInterval';
import Captcha from '../../components/Captcha';

const LoginPage = () => {
    const { setAccessToken, setPagePaths } = useContext(AppContext);

    const restfulManager = new RestfulManager();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    const [currentDateTime, setCurrentDateTime] = useState('');
    const [openSnackbar, setOpenSnackBar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const captchaRef = useRef(null);

    const loadClock = () => {
        const now = new Date();
        const dateTimeDisplay = getDateTimeDisplay(now);
        setCurrentDateTime(dateTimeDisplay);
    };

    const login = async () => {
        try {
            const isValid = captchaRef.current?.validate();
            if (!isValid) {
                setSnackbarSeverity('error');
                setSnackbarMessage('Error. Wrong characters.');
                setOpenSnackBar(true);
                return;
            }

            const res = await restfulManager.getAccessToken({
                username: username,
                password: password,
            });
            const json = await res.json();
            if ((res.status === 200) && (json.status === RES_STATUS_OK)) {
                setAccessToken(json.jwt);
                setPagePaths(json.pagePaths);
                return (<HomePage />);
            } else {
                if (json && json.msg) {
                    setSnackbarSeverity('error');
                    setSnackbarMessage(json.msg);
                    setOpenSnackBar(true);
                }
            }
        } catch (err) {
            console.error(err);
            setSnackbarSeverity('error');
            setSnackbarMessage('Error. Please contact administrator.');
            setOpenSnackBar(true);
        }
    };

    useInterval(() => {
        loadClock();
    }, 1000, []);

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <SnackbarContext.Provider value={{ setOpenSnackBar, setSnackbarSeverity, setSnackbarMessage }}>
                    <Box sx={{ width: '1920px', height: '1080px', backgroundColor: StyleConstants.mainBgColor, margin: '0px', flexGrow: 1 }}>
                        <Stack direction="column">
                            <Stack direction="row" sx={{ height: '55px', borderBottom: '3px solid ' + StyleConstants.borderColor }}>
                                <Stack direction="row" sx={{ width: '75%', alignItems: 'center' }}>
                                    <img src={MTRLogoImage} alt="Logo" style={{ width: '51px', height: '42px', margin: '3px' }} />
                                    <Typography sx={{ ...Styles.contentFont4 }}>
                                        Phase 5 LRV - Intelligent Vehicle Monitoring System (iVMS)
                                    </Typography>
                                </Stack>
                                <Stack direction="row" sx={{ width: '25%', textAlign: 'right' }}>
                                    <Typography sx={{ ...Styles.contentFont4, width: '100%', marginRight: '3px' }}>
                                        {currentDateTime}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" sx={{ height: 'calc(1080px - 55px - 3px)' }}>
                                <Box sx={{ flexGrow: 1, padding: '8px' }}>
                                    <Stack direction="column">
                                        <Stack direction="row" sx={{ alignItems: 'center', marginTop: '8px' }}>
                                            <Typography sx={{ ...Styles.contentFont3, marginRight: '8px', width: '160px' }}>
                                                Username:
                                            </Typography>
                                            <TextField
                                                variant='outlined'
                                                size='small'
                                                sx={{
                                                    width: '400px',
                                                    border: '1px solid ' + StyleConstants.borderColor,
                                                }}
                                                inputProps={{
                                                    maxLength: 20,
                                                    style: {
                                                        color: StyleConstants.borderColor,
                                                        fontSize: '24px',
                                                    }
                                                }}
                                                value={username}
                                                onChange={e => {
                                                    setUsername(e.target.value);
                                                }}
                                            />
                                        </Stack>

                                        <Stack direction="row" sx={{ alignItems: 'center', marginTop: '8px' }}>
                                            <Typography sx={{ ...Styles.contentFont3, marginRight: '8px', width: '160px' }}>
                                                Password:
                                            </Typography>
                                            <TextField
                                                variant='outlined'
                                                type="password"
                                                size='small'
                                                sx={{
                                                    width: '400px',
                                                    border: '1px solid ' + StyleConstants.borderColor,
                                                }}
                                                inputProps={{
                                                    maxLength: 20,
                                                    style: {
                                                        color: StyleConstants.borderColor,
                                                        fontSize: '24px',
                                                    }
                                                }}
                                                value={password}
                                                onChange={e => {
                                                    setPassword(e.target.value);
                                                }}
                                            />
                                        </Stack>

                                        <Stack direction="row" sx={{ alignItems: 'center', marginTop: '8px' }}>
                                            <Typography sx={{ ...Styles.contentFont3, marginRight: '8px', width: '160px' }}>
                                                &nbsp;
                                            </Typography>
                                            <Captcha 
                                                ref={captchaRef} 
                                            />
                                        </Stack>
                                    </Stack>

                                    <Button
                                        onClick={() => {
                                            login();
                                        }}
                                        sx={{ marginTop: '8px', width: '700px', backgroundColor: StyleConstants.componentBgColor, color: StyleConstants.fontColor }}
                                    >
                                        <Typography sx={{ ...Styles.contentFont3 }}>
                                            Login
                                        </Typography>
                                    </Button>
                                </Box>
                            </Stack>
                        </Stack>
                        <Snackbar
                            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                            open={openSnackbar}
                            autoHideDuration={5000}
                            onClose={() => {
                                setOpenSnackBar(false);
                            }}
                        >
                            <Alert
                                onClose={() => {
                                    setOpenSnackBar(false);
                                }}
                                severity={snackbarSeverity}
                                variant="filled"
                                sx={{ fontSize: '24px', alignItems: 'center' }}
                            >
                                {snackbarMessage}
                            </Alert>
                        </Snackbar>
                    </Box>
                </SnackbarContext.Provider>
            </LocalizationProvider>
        </>
    );
};

export default LoginPage;